import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy, Target, Activity, Users
} from 'lucide-react';
import { theme } from '../../theme';
import { ArenaFeed } from '../organisms/ArenaFeed';
import { ArenaStats } from '../organisms/ArenaStats';
import { ArenaSegments } from '../organisms/ArenaSegments';
import { ArenaProfile } from '../organisms/ArenaProfile';

export type ArenaView = 'dashboard' | 'feed' | 'segments' | 'profile';

export function Arena() {
  const [activeView, setActiveView] = useState<ArenaView>('dashboard');

  // Get user from global auth (passed from App component via localStorage)
  const user = JSON.parse(localStorage.getItem('user_info') || '{}');

  const navItems: { id: ArenaView; label: string; icon: any }[] = [
    { id: 'dashboard', label: 'Dashboard', icon: Trophy },
    { id: 'feed', label: 'Activity Feed', icon: Activity },
    { id: 'segments', label: 'Segments', icon: Target },
    { id: 'profile', label: 'Profile', icon: Users },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.semantic.bg.page,
      color: theme.semantic.text.primary,
    }}>
      {/* Arena Header */}
      <div style={{
        background: theme.semantic.bg.elevated,
        borderBottom: `2px solid ${theme.brand.crimson}`,
        padding: theme.spacing[6],
        overflowX: 'hidden',
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: theme.spacing[4],
            flexWrap: 'wrap',
            gap: theme.spacing[3],
          }}>
            <div>
              <h1 style={{
                fontFamily: theme.typography.fontFamily.display,
                fontSize: theme.typography.fontSize['2xl'],
                fontWeight: theme.typography.fontWeight.black,
                color: theme.semantic.text.primary,
                letterSpacing: theme.typography.letterSpacing.widest,
                textTransform: 'uppercase' as const,
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing[3],
              }}>
                <Trophy size={32} />
                THE ARENA
              </h1>
              <p style={{
                color: theme.semantic.text.secondary,
                marginTop: theme.spacing[1],
              }}>
                Welcome back, {user?.username}
              </p>
            </div>

          </div>

          {/* Navigation Tabs */}
          <div style={{
            display: 'flex',
            gap: theme.spacing[2],
            flexWrap: 'wrap',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin',
          }}>
            {navItems.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveView(id)}
                style={{
                  background: activeView === id ? theme.semantic.action.primary : 'transparent',
                  color: activeView === id ? theme.semantic.text.primary : theme.semantic.text.secondary,
                  border: `2px solid ${activeView === id ? theme.semantic.border.strong : theme.semantic.border.default}`,
                  padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
                  borderRadius: theme.radii.md,
                  fontFamily: theme.typography.fontFamily.sans,
                  fontSize: theme.typography.fontSize.sm,
                  fontWeight: activeView === id ? theme.typography.fontWeight.bold : theme.typography.fontWeight.medium,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing[2],
                }}
                onMouseEnter={(e) => {
                  if (activeView !== id) {
                    e.currentTarget.style.borderColor = theme.semantic.border.copper;
                    e.currentTarget.style.color = theme.semantic.text.primary;
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeView !== id) {
                    e.currentTarget.style.borderColor = theme.semantic.border.default;
                    e.currentTarget.style.color = theme.semantic.text.secondary;
                  }
                }}
              >
                <Icon size={16} />
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: theme.spacing[6],
        overflow: 'hidden',
      }}>
        <AnimatePresence mode="wait">
          {activeView === 'dashboard' && (
            <motion.div
              key="dashboard"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <ArenaStats />
            </motion.div>
          )}

          {activeView === 'feed' && (
            <motion.div
              key="feed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <ArenaFeed user={user!} />
            </motion.div>
          )}

          {activeView === 'segments' && (
            <motion.div
              key="segments"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <ArenaSegments />
            </motion.div>
          )}

          {activeView === 'profile' && (
            <motion.div
              key="profile"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              <ArenaProfile user={user!} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}